import type { ProcessingDataClassification } from '@avisoma//shared/dist/ProcessingData';
import { FaQuestionCircle } from 'react-icons/fa';
import { Link } from './Link';
import backend from '../services/backend';
import { Dialog, DialogBody, DialogTitle } from '../catalyst/dialog';
import { Button } from '../catalyst/button';
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import type { ApiEndpoints } from '@avisoma/shared';
import { useQuery } from '@tanstack/react-query';
import { LazyImage } from './LazyImage';
import { pagePreviewSrc } from '../lib/pagePreviewSrc';
import { confidenceBandText, runnerUpMarginText } from '../lib/confidenceBand';
import { PagePreviewHoverProvider } from './pagePreview/usePagePreviewHover';
import { PagePreviewHoverCard } from './pagePreview/PagePreviewHoverCard';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import {
  resolveClassificationRows,
  type ResolvedClassificationRow,
} from '../lib/classificationCategoryResolver';
import { useCategoryCatalogue } from '../hooks/useCategoryCatalogue';

/**
 * Top-scoring row — for summaries (e.g. DocumentView collapsible header).
 *
 * Takes RESOLVED rows (DR-3784): a collapsed header built from the raw ranking printed the
 * stale stored name while the expanded table beneath it showed that same row struck through.
 * A row that no longer resolves still prints its stored name here — the summary is display-only
 * — but carries the same suffix the table uses, so the two agree.
 */
export function getWinningClassificationSummary(rows: ResolvedClassificationRow[]): string | undefined {
  if (!rows.length) {
    return undefined;
  }
  const winner = rows.reduce((best, row) => (row.score > best.score ? row : best), rows[0]);
  const name = winner.live ? winner.name : `${winner.storedName} ${DEAD_ROW_SUFFIX}`;
  return `${name} · ${(winner.score * 100).toFixed(0)}%`;
}

/**
 * How a row that resolved to no live category is worded. One wording for both shapes: whether
 * the row carried an id or only a name, all the UI can honestly say is that nothing live
 * answers to it.
 */
const DEAD_ROW_SUFFIX = '(not found)';
const DEAD_ROW_TITLE = 'No live category has this name (renamed or removed)';

export function DocumentClassificationDetails({
  classification,
  scope,
}: {
  classification: ProcessingDataClassification | undefined;
  /**
   * Which vocabulary the ranking was computed against. A document-level ranking can name a
   * supertype as well as a page category, so it resolves against a wider catalogue.
   */
  scope: 'page' | 'document';
}) {
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [clusterId, setClusterId] = useState<number | undefined>(undefined);
  const catalogue = useCategoryCatalogue();
  // DR-3784: the stored names are a snapshot — resolve each row against the live catalogue so a
  // renamed category prints its current name and a deleted one is shown as gone, not as a link.
  const rows = useMemo(
    () => resolveClassificationRows(classification, scope === 'page' ? catalogue.page : catalogue.document),
    [classification, scope, catalogue],
  );

  if (!rows.length) {
    return (
      <>
        <p className="text-sm text-gray-500 dark:text-zinc-400">No classification</p>
      </>
    );
  }

  // Runner-up rows are colored by their margin from the winner (scale-invariant),
  // not their absolute score: in adapter space every runner-up scores high, so
  // "how far behind the winner" is the signal, not "how high" (DR-2073).
  const topScore = Math.max(...rows.map((row) => row.score));

  return (
    <>
      <div className="px-2 rounded-sm">
        <table className="w-full text-sm">
          <tbody>
            {rows.slice(0, 5).map((row, index) => (
              <tr key={index} className="border-b border-gray-200 dark:border-zinc-700 last:border-0">
                <th className="text-left pr-4 text-gray-600 dark:text-zinc-400 py-1">
                  <ClassificationRowName row={row} />
                </th>
                <td className="text-gray-900 dark:text-zinc-100 py-1">
                  <div className="flex items-center gap-2 justify-end">
                    <span
                      className={
                        row.score >= topScore ? confidenceBandText(row.score) : runnerUpMarginText(topScore - row.score)
                      }
                    >
                      {(row.score * 100).toFixed(0)}%
                    </span>
                    {row.clusterId !== undefined && (
                      <span>
                        <FaQuestionCircle
                          className="cursor-pointer"
                          onClick={() => {
                            setClusterId(row.clusterId);
                            setShowDetailDialog(true);
                          }}
                        />
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CategoryClusterModal show={showDetailDialog} clusterId={clusterId} hide={() => setShowDetailDialog(false)} />
    </>
  );
}

/**
 * A row's category name. A row whose category no longer exists shows the name the snapshot
 * stored, struck through and unlinked — there is nothing to navigate to, and the name must not
 * read as something the user can still pick.
 */
function ClassificationRowName({ row }: { row: ResolvedClassificationRow }) {
  if (!row.live) {
    return (
      <span title={DEAD_ROW_TITLE}>
        <span className="text-gray-400 dark:text-zinc-500 line-through">{row.storedName}</span>
        <span className="ml-1 text-xs text-gray-400 dark:text-zinc-500">{DEAD_ROW_SUFFIX}</span>
      </span>
    );
  }

  if (row.categoryId !== undefined) {
    return <Link to={`/categories/${row.categoryId}`}>{row.name}</Link>;
  }

  return <>{row.name}</>;
}

function CategoryClusterModal({
  show,
  clusterId,
  hide,
}: {
  show: boolean;
  clusterId: number | undefined;
  hide: () => void;
}) {
  const { data: cluster, error } = useQuery({
    // String key so this shares the cache (and invalidations) with the dedicated
    // cluster page, which keys by the string route param.
    queryKey: ['categoryCluster', clusterId != null ? String(clusterId) : undefined],
    queryFn: () => backend.getCategoryCluster(clusterId!.toString()).then((res) => res.data),
    enabled: show && !!clusterId,
  });

  return (
    <>
      <Dialog open={show} onClose={hide} size="6xl">
        <DialogTitle className="flex justify-between items-center">
          {cluster ? (
            <>
              <span>
                <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-zinc-800 whitespace-nowrap">{cluster?.item.categoryName}</span>{' '}
                Cluster #{clusterId} Details
              </span>
            </>
          ) : error ? (
            'Cluster not found'
          ) : (
            'Loading...'
          )}
          <div className="flex items-center gap-2">
            {clusterId != null && (
              <Link
                to={`/category-clusters/${clusterId}`}
                className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
              >
                Open full cluster page
                <ArrowTopRightOnSquareIcon className="size-4" />
              </Link>
            )}
            <Button outline onClick={hide}>
              <XMarkIcon className="size-4" />
            </Button>
          </div>
        </DialogTitle>
        <DialogBody>
          <PagePreviews pages={cluster?.pages ?? []} category={cluster?.item.categoryName} />
        </DialogBody>
      </Dialog>
    </>
  );
}

export function PagePreviews({
  pages,
  category,
}: {
  pages: ApiEndpoints<string>['/api/category-clusters/:id']['GET']['response']['pages'];
  category: string | undefined;
}) {
  const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
  const [page, setPage] = useState<
    ApiEndpoints<string>['/api/category-clusters/:id']['GET']['response']['pages'][number] | undefined
  >(undefined);

  const handleDocumentPreviewClick = (
    page: ApiEndpoints<string>['/api/category-clusters/:id']['GET']['response']['pages'][number],
  ) => {
    setPage(page);
    setShowDocumentPreviewModal(true);
  };

  const shownPages = pages.slice(0, 8);

  return (
    <>
      <PagePreviewHoverProvider visibleIds={new Set(shownPages.map((p) => p.id))}>
        <div className="@container">
          <div className="grid grid-cols-1 @md:grid-cols-4 gap-4">
            {shownPages.map((page) => (
              <PagePreviewHoverCard
                key={page.id}
                page={{ id: page.id, classification: page.classification }}
                className="cursor-pointer hover:scale-102 transition-all duration-200"
              >
                <div className="aspect-3/4 group relative" onClick={() => handleDocumentPreviewClick(page)}>
                  {/* <Link to={`/document-pages/${page.id}`}> */}
                  <LazyImage src={pagePreviewSrc(page.id, { resize: 'x700' })} className="w-full h-full" />
                  {/* </Link> */}
                  {/* <FullScreenImagePreviewTrigger pageId={page.id} /> */}
                </div>
                <Link
                  to={`/document-pages/${page.id}`}
                  className={twMerge(
                    clsx('text-xs text-gray-500 dark:text-zinc-400 truncate text-center font-bold block', {
                      'text-red-500 dark:text-red-400': page.classificationCategory !== category,
                    }),
                  )}
                >
                  {page.classificationCategory}
                </Link>
                <div className="text-xs text-gray-500 dark:text-zinc-400 truncate text-center">
                  {(page.classificationScore * 100).toFixed(2)}%
                </div>
              </PagePreviewHoverCard>
            ))}
          </div>
        </div>
      </PagePreviewHoverProvider>

      <DocumentPreviewModal
        show={showDocumentPreviewModal}
        page={page}
        hide={() => setShowDocumentPreviewModal(false)}
      />
    </>
  );
}

function DocumentPreviewModal({
  show,
  page,
  hide,
}: {
  show: boolean;
  page: ApiEndpoints<string>['/api/category-clusters/:id']['GET']['response']['pages'][number] | undefined;
  hide: () => void;
}) {
  if (!page) {
    return null;
  }

  return (
    <>
      <Dialog open={show} onClose={hide} size="6xl">
        <DialogTitle className="flex justify-between items-center">
          <div className="gap-2 flex items-center">
            <span>Page Preview</span>
            <Link to={`/document-pages/${page.id}`} target="_blank">
              <ArrowTopRightOnSquareIcon className="size-4" />
            </Link>
          </div>
          <Button outline onClick={hide}>
            <XMarkIcon className="size-4" />
          </Button>
        </DialogTitle>
        <DialogBody>
          <div className="gap-2 flex flex-col">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">{page.classificationData?.title}</h2>
            <p className="text-sm text-gray-500 dark:text-zinc-400">{page.classificationData?.description}</p>
            <div className="p-3 bg-gray-100 dark:bg-zinc-800 rounded-lg">
              <LazyImage src={pagePreviewSrc(page.id)} className="w-full h-full" />
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
}
